/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * setBarba.js
 */

/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
import barba from '@barba/core';
import gsap from 'gsap';
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
import { setDataCounter } from "./setDataCounter";
import { setScrollBar } from "./setScrollBar";
import { setCreateSpan } from "./setCreateSpan";
import { setScrollTrigger } from './setScrollTrigger';
import { setCreateButton } from "./setCreateButton";

function setBarba() {
    const html = document.querySelector('html');

    /**
     * metaタグの設定
     * @description ページ遷移時にmetaタグを更新する
     * @param {*} target 
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    const replaceHeadTags = target => {
        const head = document.head;
        const targetHead = target.html.match(/<head[^>]*>([\s\S.]*)<\/head>/i)[0];
        const newPageHead = document.createElement('head');
        newPageHead.innerHTML = targetHead;
        const removeHeadTags = [
            "meta[name='description']",
            "meta[property^='og']",
            "meta[name^='twitter']",
            "meta[name='robots']",
            'meta[itemprop]',
            'link[itemprop]',
            "link[rel='prev']",
            "link[rel='next']",
            "link[rel='canonical']"
        ].join(',');
        const headTags = [...head.querySelectorAll(removeHeadTags)];
        headTags.forEach(item => {
            head.removeChild(item);
        });
        const newHeadTags = [...newPageHead.querySelectorAll(removeHeadTags)];
        newHeadTags.forEach(item => {
            head.appendChild(item);
        });
    };

    /**
     * Googleアナリティクスの設定
     * @description Googleアナリティクスに情報を送る
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    barba.hooks.after(() => {
        ga('set', 'page', window.location.pathname);
        ga('send', 'pageview');
    });

    /**
     * Preventリンクの拡張
     * @description 非同期遷移を有効・無効にする設定
     * @param {object} el - 対象のリンク要素
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    function preventSettings(el) {
        let targtHref = el.getAttribute('href');

        //同URLはクリックできないようにする
        if (targtHref == location.href) {
            return true;
        }

        // アンカーリンクであり同一ページでなければbarbaを有効に
        if (targtHref.startsWith('#') && targtHref != location.hash) {
            return false;
        }

        //hal_cmsが含まれて拡張子がなければbarbaを無効に
        if (targtHref.includes('hal_cms') && !/\.(xlsx?|docx?|pptx?|pdf|jpe?g|png|gif|svg)/.test(targtHref.toLowerCase())) {
            return true;
        }

        // 拡張子が該当する場合はtarget="_blank"に
        if (/\.(xlsx?|docx?|pptx?|pdf|jpe?g|png|gif|svg)/.test(targtHref.toLowerCase())) {
            el.setAttribute('target', '_blank');
            return true;
        }

        // 該当クラスに属していればBarbaを無効に
        let ignoreClasses = ['ab-item'];
        ignoreClasses.forEach((cls) => {
            if (el.classList.contains(cls)) {
                return true;
            }
        })
    }

    /**
     * ContactForm7
     * @description ContactForm7の再読み込み
     * @param {object} next - 次のページの情報
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    function contactForm7Run(next) {
        let cf_forms = next.container.querySelector('div.wpcf7 > form');
        wpcf7.init(cf_forms);
    }

    /**
     * 遅延処理
     * @description 遅延処理を行う
     * @param {number} n - 遅延時間
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    function delay(n) {
        n = n || 2000;
        return new Promise((done) => {
            setTimeout(() => {
                done();
            }, n);
        });
    }

    /**
     * アンカーリンクの設定
     */
    function setAnchorLink() {
        let hash = location.hash;
        let headerHeight = document.querySelector('header').offsetHeight;
        if (hash) {
            let targetId = document.getElementById(hash.replace('#', ''));
            let targetHeight40 = targetId.offsetHeight * 0.4;
            let targetOffsetTop = window.pageYOffset + targetId.getBoundingClientRect().top - headerHeight - targetHeight40;
            window.scrollTo({
                top: targetOffsetTop,
                behavior: 'smooth'
            });
        }
    }

    /**
     * 遷移アニメーション
     * @description ページ遷移時のアニメーション
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    //ページ共通 - 遷移中アニメーション
    function pageTransition() {
        let transitionItem__first = document.querySelector(".--item__first");
        let transitionItem__second = document.querySelector(".--item__second");
        let tl = gsap.timeline();
        tl.to([transitionItem__second], { duration: 1, transformOrigin: "left", scaleX: 1, ease: "Quart.easeInOut" })
            .to([transitionItem__first], { duration: 0.75, transformOrigin: "left", scaleX: 1, ease: "Quart.easeOut" }, "<0.5")
            .to([transitionItem__first], { duration: 1, transformOrigin: "right", scaleX: 0, ease: "Quart.easeInOut" })
            .to([transitionItem__second], { duration: 0.75, transformOrigin: "right", scaleX: 0, ease: "Quart.easeOut" }, "<0.5")
    }

    //ページ共通 - 遷移前のアニメーション
    function leaveAnimation() {
        const tl = gsap.timeline();
        tl.to(['main', 'footer'], { duration: 1, opacity: 0, ease: 'Quart.easeOut' });
        html.classList.add("--loading");
        html.classList.remove("--open");
    }

    //ページ共通 - 遷移後のアニメーション
    function enterAnimation() {
        const tl = gsap.timeline();
        tl.from(['main'], { duration: 1, opacity: 0, ease: 'Quart.easeOut' });
        tl.to(['footer'], {
            duration: 1, opacity: 1, ease: 'Quart.easeOut',
            onComplete: () => { html.classList.remove("--loading"); }
        }, '<');
    }

    //ページ別 - タイトルのアニメーション
    function titleAnimation(type) {
        setCreateSpan();
        let tl = gsap.timeline({ delay: 1, });

        //トップページ
        const home = () => {
            let copy = document.querySelectorAll(".mv .copy .text-line > span");
            tl.to([copy], { duration: 1.5, y: 0, rotateZ: 0, ease: "Quart.easeInOut", stagger: 0.1, })
            setTimeout(() => { setDataCounter() }, 100);
        }

        //下層ページ
        const lower = () => {
            let title = document.querySelectorAll(".content__title h1 .--js_createSpan > span");
            let subTitle = document.querySelectorAll(".content__title p > span");
            let img = document.querySelectorAll(".content__title__mv .img");
            let alpha = document.querySelectorAll(".--alpha");
            tl.to([title], { duration: 1, y: 0, rotateZ: 0, ease: "Quart.easeInOut", stagger: 0.05, })
                .to([subTitle], { duration: 1, y: 0, rotateZ: 0, ease: "Quart.easeInOut", stagger: 0.05, }, "<")
                .to([img, alpha], { duration: 1, scale: 1, opacity: 1, ease: "Quart.easeInOut", }, "<")
        }

        type === "home" ? home() : lower();
    }

    function setPrivacyPolicy() {
        let button = document.querySelector('.sec__privacy-policy .button-wrapper');
        button.remove();
    }

    /**
     * 実行
     * @description Barba.jsの設定
     * transitionは、遷移前後のアニメーションを記述
     * viewsは、ページごとのアニメーションを記述
     *━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    barba.init({
        sync: true,
        prevent: ({ el }) => preventSettings(el),
        transitions: [
            {
                async leave(data) {
                    const done = this.async();
                    leaveAnimation();
                    pageTransition();
                    await delay(600);
                    done();
                },
                beforeEnter({ next }) {
                    window.scrollTo(0, 0);
                    replaceHeadTags(next);
                    titleAnimation(next.namespace);
                    next.namespace === "contact" && contactForm7Run(next);
                    next.namespace === "request" && contactForm7Run(next);
                    next.namespace === "privacy-policy" && setPrivacyPolicy();
                },
                async enter(data) {
                    await delay(800);
                    enterAnimation();
                },
                after({ next }) {
                    setScrollTrigger();
                    setCreateButton();
                    setAnchorLink();
                    setTimeout(() => { setScrollBar() }, 1000);
                }
            }
        ]
    });
}

export { setBarba };